import ShokzMember from '@theme/shokz-member';

/**
 * Brand Day lottery. Logged-in customers register first (existing members
 * continue) and then draw. Guests draw without an account. A repeat entry
 * leaves the wheel still and opens the repeat popup. Claim now goes to
 * checkout. Sign in to claim opens login and returns to the Hub.
 * If the logged-in account email differs from the entered email, the claim
 * CTA logs out and sends them through login again. On submit, an email that
 * is unregistered or registered but not a ShokzSquad member is registered
 * via us-shopify-register.
 */
const brandDayLotteryAuth = {
  token: null,
  timestamp: null,
  pending: null,

  settings() {
    return window.shokzLotteryApiSettings || {};
  },

  async ensureToken(force) {
    if (!force && this.token && this.timestamp) {
      return { token: this.token, timestamp: this.timestamp };
    }
    if (this.pending && !force) return this.pending;

    this.pending = (async () => {
      const settings = this.settings();
      const baseUrl = String(settings.baseUrl || '').replace(/\/$/, '');
      if (!baseUrl || !settings.apiKeyQuery) {
        throw new Error('Lottery API not configured');
      }
      const res = await (window.originFetch || fetch)(baseUrl + '/api/v1/get-token', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'X-Site': settings.site || 'us',
          'Api-Key': settings.apiKeyQuery,
        },
      });
      const json = await res.json().catch(function () { return {}; });
      if (!res.ok || json.code !== 0 || !json.data || !json.data.token) {
        this.token = null;
        this.timestamp = null;
        throw new Error('Token request failed');
      }
      this.token = String(json.data.token);
      this.timestamp = String(json.data.timestamp);
      return { token: this.token, timestamp: this.timestamp };
    })();

    try {
      return await this.pending;
    } finally {
      this.pending = null;
    }
  },

  async prizeDraw(email, activityId, retry) {
    const settings = this.settings();
    const baseUrl = String(settings.baseUrl || '').replace(/\/$/, '');
    const apiKey = settings.apiKeyDraw || settings.apiKeyQuery;
    if (!baseUrl || !apiKey) throw new Error('Lottery API not configured');

    const auth = await this.ensureToken(false);
    const form = new FormData();
    form.append('email', email);
    form.append('activity_id', String(activityId));

    const res = await (window.originFetch || fetch)(baseUrl + '/api/v1/lottery/prize-draw', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'X-Site': settings.site || 'us',
        'Api-Key': apiKey,
        'X-Verify-Token': auth.token,
        Timestamp: auth.timestamp,
      },
      body: form,
    });

    if ((res.status === 401 || res.status === 403) && retry !== false) {
      this.token = null;
      this.timestamp = null;
      await this.ensureToken(true);
      return this.prizeDraw(email, activityId, false);
    }

    const json = await res.json().catch(function () { return {}; });
    if (json.code !== 0) {
      const err = new Error(json.message || json.msg || 'Lottery request failed');
      err.lotteryBusinessCode = json.code;
      throw err;
    }
    return json;
  },

  /**
   * 查询该邮箱已参与抽奖的结果（repeat entry / no_finish 时用于还原中奖结果）。
   * prize-draw 在 act_status = no_finish 时只返回状态，不带奖品；这里用
   * prize-query 拿到之前的记录（prize_id 为数字库表 id，对应 prizeMap.price_id）。
   */
  async prizeQuery(email, activityId, retry) {
    const settings = this.settings();
    const baseUrl = String(settings.baseUrl || '').replace(/\/$/, '');
    const apiKey = settings.apiKeyQuery;
    if (!baseUrl || !apiKey) throw new Error('Lottery API not configured');

    const auth = await this.ensureToken(false);
    const form = new FormData();
    form.append('email', email);
    form.append('activity_id', String(activityId));

    const res = await (window.originFetch || fetch)(baseUrl + '/api/v1/lottery/prize-query', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'X-Site': settings.site || 'us',
        'Api-Key': apiKey,
        'X-Verify-Token': auth.token,
        Timestamp: auth.timestamp,
      },
      body: form,
    });

    if ((res.status === 401 || res.status === 403) && retry !== false) {
      this.token = null;
      this.timestamp = null;
      await this.ensureToken(true);
      return this.prizeQuery(email, activityId, false);
    }

    const json = await res.json().catch(function () { return {}; });
    if (json.code !== 0) {
      throw new Error(json.message || json.msg || 'Lottery query failed');
    }
    return json;
  },
};

function initBrandDayLotteries() {
  document.querySelectorAll('[data-brand-day-lottery]').forEach(function (root) {
    if (!root || root.dataset.lotteryBound === 'true') return;
    root.dataset.lotteryBound = 'true';

    const configScript = root.querySelector('script[data-roulette-config]');
    if (!configScript) return;
    let cfg;
    try {
      cfg = JSON.parse(configScript.textContent);
    } catch (e) {
      return;
    }

    const apiUrl = cfg.apiUrl;
    const actId = cfg.actId;
    const almostPrizeKey = buildPrizeKey(cfg.actId, cfg.almostPriceId);
    const almostSectorDegDesktop = Number(cfg.almostSectorDegDesktop);
    const almostSectorDegMobile = Number(cfg.almostSectorDegMobile);
    const missSectorDegDesktop = Number(cfg.missSectorDegDesktop);
    const missSectorDegMobile = Number(cfg.missSectorDegMobile);
    const spinTotalMs = Number(cfg.spinTotalMs) || 4600;
    const decelMs = Number(cfg.decelMs) || 1000;
    // 注册链路与抽奖链路分开兜底：注册失败不允许抽奖。
    // 注册链（getMemberInfoByEmail / us-shopify-register / us-hub-register）单独 30s；
    // 抽奖链（get-token / prize-draw / prize-query）单独 30s。
    const registerTimeoutMs = 30000;
    const drawTimeoutMs = 30000;
    const ga4ButtonName = cfg.ga4ButtonName || 'Spin_To_Win';
    const ga4ModuleName = cfg.ga4ModuleName || '';
    const klaviyoFormClass = cfg.klaviyoFormClass;
    const usageScenarios = cfg.usageScenarios || 'cs';

    // Prizes are matched by the composite key actId + priceId (string concatenation)
    // rather than a single actPriceId. actId is the section-level activity ID; each
    // prize only configures price_id. The backend returns both fields; we concat them
    // and compare against actId + the block's configured price_id.
    function buildPrizeKey(actId, priceId) {
      return String(actId == null ? '' : actId) + String(priceId == null ? '' : priceId);
    }

    // Derive the three lookup tables (formerly hardcoded) from prize blocks.
    const prizes = Array.isArray(cfg.prizes) ? cfg.prizes : [];
    const validPrizeKeys = new Set();
    const prizeKeyToSectorDegDesktop = {};
    const prizeKeyToSectorDegMobile = {};
    const prizeKeyToPrizeMeta = {};
    // prize-query returns prize_id as the string sector code (e.g. openrun_pro_2_lunar_silver),
    // which equals the block's lottery_prize_code. Map that code back to the composite key.
    const prizeCodeToKey = {};
    prizes.forEach(function (p) {
      const key = buildPrizeKey(actId, p.priceId);
      if (!key) return;
      validPrizeKeys.add(key);
      prizeKeyToSectorDegDesktop[key] = Number(p.sectorDegDesktop);
      prizeKeyToSectorDegMobile[key] = Number(p.sectorDegMobile);
      prizeKeyToPrizeMeta[key] = {
        type: p.type || 'miss',
        name: p.type === 'product' ? p.resultName || p.value || '' : undefined,
        amount: p.type === 'coupon' ? p.resultName || p.value || '' : undefined,
        headingOverride: p.headingOverride || '',
        subOverride: p.subOverride || '',
        variantIds: Array.isArray(p.variantIds) ? p.variantIds : [],
        prizeCode: p.prizeCode || '',
      };
      if (p.prizeCode) prizeCodeToKey[p.prizeCode] = key;
    });
    // Miss is its own outcome (not a prize block), so its copy is always the
    // "miss" template regardless of fallback_act_price_id.
    const fallbackMeta = { type: 'miss' };

    const resultTemplates = (cfg.copy && cfg.copy.resultTemplates) || {};
    const actStatusResultCopy = (cfg.copy && cfg.copy.blocked) || {};

    function isMobile() {
      return !window.matchMedia('(min-width: 901px)').matches;
    }

    function getSectorDeg(prizeKey) {
      const map = isMobile() ? prizeKeyToSectorDegMobile : prizeKeyToSectorDegDesktop;
      return map[prizeKey] ?? 180;
    }

    // "almost" is a real prize that isn't shown on the wheel, and "miss" is a true
    // non-win. Neither is a carousel block, so both angles are section-level settings.
    function getAlmostSectorDeg() {
      const deg = isMobile() ? almostSectorDegMobile : almostSectorDegDesktop;
      return Number.isFinite(deg) ? deg : 0;
    }
    function getMissSectorDeg() {
      const deg = isMobile() ? missSectorDegMobile : missSectorDegDesktop;
      return Number.isFinite(deg) ? deg : 0;
    }

    const klaviyoContainer = klaviyoFormClass ? root.querySelector('.' + klaviyoFormClass) : null;

    root.querySelectorAll('.prize-cards__info').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.stopPropagation();
        const willOpen = !btn.classList.contains('is-open');
        root.querySelectorAll('.prize-cards__info.is-open').forEach(function (openBtn) {
          openBtn.classList.remove('is-open');
          openBtn.setAttribute('aria-expanded', 'false');
        });
        if (willOpen) {
          btn.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', function () {
      root.querySelectorAll('.prize-cards__info.is-open').forEach(function (openBtn) {
        openBtn.classList.remove('is-open');
        openBtn.setAttribute('aria-expanded', 'false');
      });
    });

    if (!klaviyoContainer) return;

    const agreementEl = root.querySelector('.agreement-checkbox');
    const h2El = root.querySelector('[data-lottery-heading]');
    const descEl = root.querySelector('[data-lottery-desc]');
    const resultSubEl = root.querySelector('[data-lottery-result-sub]');

    function isElementVisible(el) {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    function getActiveWheelImg() {
      const imgs = root.querySelectorAll('.wheel-img');
      for (const img of imgs) {
        const wrap = img.closest('.wheel-wrap');
        if (!wrap) continue;
        if (isElementVisible(wrap)) return img;
      }
      return imgs[0] || null;
    }

    let buttonHasAppeared = false;
    let retryCount = 0;
    const maxRetries = 8;
    const retryInterval = 900;
    let buttonClicked = false;
    let buttonClickedAtMs = 0;
    let submittedEmail = '';
    let completed = false;
    let spinStartedAtMs = 0;
    let submitAbortTimerId = 0;

    // 留邮（Klaviyo）与注册解耦后：提交瞬间就发起「注册 → 抽奖」链的 promise。
    // 抽奖仍依赖注册（fetchPrize 内部 register → draw），但不再依赖 Klaviyo 留邮成功。
    let pendingDrawPromise = null;
    // Klaviyo 留邮是否已确认成功（klaviyoForms submit 事件，或 DOM 输入框消失兜底）。
    let klaviyoSubmitConfirmed = false;
    // 留邮失败只重试一次（不再无限重试）：klaviyoMaxRetries 为最大额外重试次数。
    let klaviyoRetryCount = 0;
    let klaviyoRetryDeferral = 0;
    const klaviyoMaxRetries = 1;
    const klaviyoRetryInterval = 1500;
    let klaviyoRetryTimerId = 0;
    // 程序化重试点击 Klaviyo 提交按钮时置位，避免委托 click 监听把这次重试又当成一次新的用户提交。
    let retrySubmitting = false;
    // 留邮 subscribe 埋点是否已上报（success/fail 各只报一次，避免重复）。
    let emailSubscribeFired = false;

    // 留邮 subscribe 埋点：module = 活动，button_name = 按钮，status = success / fail。
    function fireEmailSubscribe(status) {
      if (emailSubscribeFired) return;
      emailSubscribeFired = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push({
        event: 'ga4Event',
        event_name: 'subscribe',
        event_parameters: {
          module: ga4ModuleName,
          status: status,
          button_name: ga4ButtonName,
        },
      });
    }

    // 从 klaviyoFormClass（如 klaviyo-form-UtVBrN）推导 Klaviyo Form ID，用于监听 submit。
    const klaviyoFormId = klaviyoFormClass ? String(klaviyoFormClass).replace(/^klaviyo-form-/, '') : '';

    // 监听 Klaviyo 留邮成功：Klaviyo 的 klaviyoForms `submit` 在「成功提交后」才触发
    // （非法邮箱 / 接口失败不会触发 submit，而是展示内联校验错误），所以 submit 即可当作成功信号。
    if (klaviyoFormId) {
      window.addEventListener('klaviyoForms', function (e) {
        const d = e && e.detail;
        if (!d || d.type !== 'submit') return;
        const fid = String(d.formId || d.formID || '').toLowerCase();
        if (fid !== klaviyoFormId.toLowerCase()) return;
        klaviyoSubmitConfirmed = true;
        fireEmailSubscribe('success');
        if (klaviyoRetryTimerId) {
          window.clearTimeout(klaviyoRetryTimerId);
          klaviyoRetryTimerId = 0;
        }
      });
    }

    function findInner() {
      const button = klaviyoContainer.querySelector('button');
      const input = klaviyoContainer.querySelector('input');
      const form = klaviyoContainer.querySelector('form');
      return { button, input, form };
    }

    function hideAllWheelHints() {
      root.querySelectorAll('[data-wheel-hint]').forEach((hint) => {
        hint.classList.remove('is-visible');
        hint.setAttribute('aria-hidden', 'true');
      });
    }

    function showWheelHintForWrap(wheelWrap) {
      const hint = wheelWrap.querySelector('[data-wheel-hint]');
      if (!hint) return;
      hideAllWheelHints();
      hint.classList.add('is-visible');
      hint.setAttribute('aria-hidden', 'false');
    }

    // Result popup — overlays the wheel with the draw result (heading + sub).
    function hideAllWheelResults() {
      root.querySelectorAll('[data-wheel-result]').forEach((el) => {
        el.classList.remove('is-visible');
        el.setAttribute('aria-hidden', 'true');
      });
    }

    function showWheelResult(copy) {
      const wheelImgEl = getActiveWheelImg();
      const wheelWrap = wheelImgEl ? wheelImgEl.closest('.wheel-wrap') : null;
      if (!wheelWrap) return;
      const result = wheelWrap.querySelector('[data-wheel-result]');
      if (!result) return;
      hideAllWheelResults();
      const headingEl = result.querySelector('[data-wheel-result-heading]');
      const subEl = result.querySelector('[data-wheel-result-sub]');
      const heading = (copy && copy.heading) || '';
      const sub = (copy && (copy.sub || copy.main)) || '';
      if (headingEl) headingEl.innerHTML = heading;
      if (subEl) subEl.textContent = sub;
      result.classList.add('is-visible');
      result.setAttribute('aria-hidden', 'false');
    }

    const promptEl = root.querySelector('[data-lottery-prompt]');
    const initialPromptText = promptEl ? promptEl.textContent : '';

    root.querySelectorAll('.wheel-img').forEach((wheelImg) => {
      wheelImg.addEventListener('click', () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event_parameters: null });
        window.dataLayer.push({
          event: 'ga4Event',
          event_name: 'select_content',
          event_parameters: {
            content_type: 'email',
            content_name: ga4ButtonName,
            button_name: 'Lottery',
          },
        });

        if (completed) return;
        const { input } = findInner();
        const email = input && input.value ? input.value.trim() : '';
        if (email) return;
        if (promptEl) promptEl.textContent = 'Please enter your email before spinning.';
        const wheelWrap = wheelImg.closest('.wheel-wrap');
        if (!wheelWrap) return;
        showWheelHintForWrap(wheelWrap);
      });
    });

    root.querySelectorAll('.wheel-hint__close').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const hint = btn.closest('[data-wheel-hint]');
        if (hint) {
          hint.classList.remove('is-visible');
          hint.setAttribute('aria-hidden', 'true');
        }
      });
    });

    root.querySelectorAll('.wheel-result__close').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const result = btn.closest('[data-wheel-result]');
        if (result) {
          result.classList.remove('is-visible');
          result.setAttribute('aria-hidden', 'true');
        }
      });
    });

    function applyButtonEnablement(button) {
      if (!button) return;
      const checked = agreementEl ? agreementEl.checked : true;
      button.style.pointerEvents = checked ? 'auto' : 'none';
      button.style.opacity = checked ? '1' : '0.5';
    }

    function isButtonHidden(button) {
      if (!button) return true;
      const style = window.getComputedStyle(button);
      return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
    }

    // Klaviyo's success state removes (or hides) the email input. This is a more
    // reliable "submit accepted" signal than the button, because some Klaviyo
    // success states keep a button (e.g. a confirm/close CTA) while still taking
    // the email field away — which would leave the button-based check stuck and
    // Klaviyo's default "Thanks for subscribing!" message visible.
    function isEmailInputGone() {
      const emailInput =
        klaviyoContainer.querySelector('input[type="email"]') ||
        klaviyoContainer.querySelector('input[type="text"]');
      if (!emailInput) return true;
      const style = window.getComputedStyle(emailInput);
      return (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        Number(style.opacity) === 0
      );
    }

    function getRotationDeg(el) {
      const transform = window.getComputedStyle(el).transform;
      if (!transform || transform === 'none') return 0;
      const match = transform.match(/matrix\(([^)]+)\)/);
      if (!match) return 0;
      const parts = match[1].split(',').map((s) => Number(s.trim()));
      const a = parts[0];
      const b = parts[1];
      return Math.atan2(b, a) * (180 / Math.PI);
    }

    function normalizeDeg(deg) {
      const x = deg % 360;
      return x < 0 ? x + 360 : x;
    }

    let claimOverride = null;
    let wonVariantIds = [];
    let wonPrizeCode = '';

    function variantIdList(ids) {
      return (Array.isArray(ids) ? ids : []).map(Number).filter(Boolean);
    }

    function configuredPrizes(code) {
      if (!code) return [];
      return []
        .concat(Array.isArray(cfg.prizes) ? cfg.prizes : [])
        .concat(Array.isArray(cfg.displayPrizes) ? cfg.displayPrizes : [])
        .filter(function (prize) {
          return prize && prize.prizeCode === code;
        });
    }

    function configuredVariantIds(code) {
      const ids = [];
      configuredPrizes(code).forEach(function (prize) {
        variantIdList(prize.variantIds).forEach(function (id) {
          if (ids.indexOf(id) === -1) ids.push(id);
        });
      });
      return ids;
    }

    async function variantIdsForPrize(code) {
      const fromConfig = configuredVariantIds(code);
      if (fromConfig.length) return fromConfig;
      const handles = [];
      configuredPrizes(code).forEach(function (prize) {
        (Array.isArray(prize.productHandles) ? prize.productHandles : []).forEach(function (handle) {
          const value = String(handle || '').trim();
          if (value && handles.indexOf(value) === -1) handles.push(value);
        });
      });
      const ids = [];
      for (let i = 0; i < handles.length; i++) {
        try {
          const res = await fetch('/products/' + encodeURIComponent(handles[i]) + '.js', {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) continue;
          const product = await res.json();
          const variants = product && Array.isArray(product.variants) ? product.variants : [];
          const variant = variants.filter(function (item) { return item && item.available; })[0] || variants[0];
          if (variant && variant.id) ids.push(Number(variant.id));
        } catch (e) {}
      }
      return variantIdList(ids);
    }

    // 每次兑奖只发「一个」变体：
    // 1) 奖品块配了 claim_variant_id → 直接用这个精确变体（一等奖锁 Lunar White）。
    // 2) 否则用奖品块 variantIds 的第一个（多变体产品不要把全部变体都加进购物车）。
    // 3) 都没配时才回落 productHandles，每个 handle 只取首个可用变体。
    async function availableVariantIdsForPrize(code) {
      const configured = configuredPrizes(code);
      for (let i = 0; i < configured.length; i++) {
        const pinned = Number(configured[i] && configured[i].claimVariantId);
        if (pinned) return [pinned];
      }

      const fromConfig = configuredVariantIds(code);
      if (fromConfig.length) return [fromConfig[0]];

      const handles = [];
      configuredPrizes(code).forEach(function (prize) {
        (Array.isArray(prize.productHandles) ? prize.productHandles : []).forEach(function (handle) {
          const value = String(handle || '').trim();
          if (value && handles.indexOf(value) === -1) handles.push(value);
        });
      });
      const ids = [];
      for (let i = 0; i < handles.length; i++) {
        try {
          const res = await fetch('/products/' + encodeURIComponent(handles[i]) + '.js', {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) continue;
          const product = await res.json();
          const variants = product && Array.isArray(product.variants) ? product.variants : [];
          const chosen = variants.filter(function (item) { return item && item.available; })[0] || variants[0];
          if (chosen && chosen.id) {
            ids.push(Number(chosen.id));
            break;
          }
        } catch (e) {}
      }
      return variantIdList(ids);
    }

    // Out-of-stock claim: keep the wheel pointing at the won prize, but swap the
    // result copy and hide the claim CTA.
    function showOutOfStock() {
      const tpl = (resultTemplates && resultTemplates.outOfStock) || {
        heading: 'Out of stock',
        sub: 'This prize is currently unavailable. Please try again later.',
      };
      root.classList.remove('is-result', 'is-result--miss', 'is-win');
      if (h2El) {
        h2El.hidden = false;
        h2El.innerHTML = tpl.heading || '';
      }
      if (descEl) descEl.textContent = tpl.sub || '';
      if (resultSubEl) {
        resultSubEl.textContent = '';
        resultSubEl.hidden = true;
      }
      root.classList.add('is-result');
      hideClaim();
    }

    function fillToken(text, token, value) {
      return String(text || '').split(token).join(value);
    }

    function customerLoggedIn() {
      return !!(window.customer && String(window.customer.id || '').length);
    }

    function normalizeEmail(value) {
      return String(value || '').trim().toLowerCase();
    }

    function accountEmailMatches(email) {
      const accountEmail = window.customer && window.customer.email;
      const left = normalizeEmail(accountEmail);
      const right = normalizeEmail(email);
      return left !== '' && right !== '' && left === right;
    }

    function claimAccountMatches() {
      if (!customerLoggedIn()) return false;
      if (!submittedEmail) return true;
      return accountEmailMatches(submittedEmail);
    }

    function claimLoginHref() {
      // 跳会员登录入口：非登录态由 /customer_authentication/login 拉起 Shopify 登录，
      // 登录成功后 return_to 回会员 Hub（/pages/shokzsquad-benefits）。
      // 若已登录但留邮邮箱与账户邮箱不一致，先退出登录再走这个入口，避免进错账户。
      const hubLogin = '/customer_authentication/login?return_to=/pages/shokzsquad-benefits';
      if (customerLoggedIn() && submittedEmail && !accountEmailMatches(submittedEmail)) {
        const logoutBase = (window.routes && window.routes.account_logout_url) || '/account/logout';
        const logoutSep = logoutBase.indexOf('?') >= 0 ? '&' : '?';
        return logoutBase + logoutSep + 'return_url=' + encodeURIComponent(hubLogin);
      }
      return hubLogin;
    }

    function prizeAlreadyClaimed() {
      const tags = window.customer && window.customer.tags;
      return Array.isArray(tags) && tags.indexOf('ShokzSquad_BD_redeemed') !== -1;
    }

    function resolveClaimState() {
      if (claimOverride) return claimOverride;
      if (!claimAccountMatches()) return 'guest';
      if (prizeAlreadyClaimed()) return 'claimed';
      return 'ready';
    }

    function showClaim(state) {
      const guest = root.querySelector('[data-lottery-claim="guest"]');
      if (guest) guest.setAttribute('href', claimLoginHref());
      root.querySelectorAll('[data-lottery-claim]').forEach(function (el) {
        el.hidden = el.getAttribute('data-lottery-claim') !== state;
      });
    }

    function hideClaim() {
      root.querySelectorAll('[data-lottery-claim]').forEach(function (el) {
        el.hidden = true;
      });
    }

    function buildResultCopy(meta) {
      const tpl = resultTemplates[meta.type] || resultTemplates.miss || { heading: '', sub: '' };
      let heading = meta.headingOverride || tpl.heading || '';
      let sub = meta.subOverride || tpl.sub || '';
      if (meta.type === 'product' && meta.name) {
        heading = fillToken(heading, '%name%', meta.name);
        sub = fillToken(sub, '%name%', meta.name);
      } else if (meta.type === 'coupon' && meta.amount) {
        heading = fillToken(heading, '%amount%', meta.amount);
        sub = fillToken(sub, '%amount%', meta.amount);
      }
      return {
        heading: heading,
        sub: sub,
        claim: meta.type === 'product' || meta.type === 'coupon',
      };
    }

    function setResultCopy(copy) {
      if (!h2El || !descEl || !resultSubEl) return;

      root.classList.remove('is-result', 'is-result--miss', 'is-win');
      h2El.hidden = false;
      h2El.innerHTML = copy.heading;
      descEl.textContent = copy.main || copy.sub;

      if (copy.main && copy.sub) {
        resultSubEl.textContent = copy.sub;
        resultSubEl.hidden = false;
      } else {
        resultSubEl.textContent = '';
        resultSubEl.hidden = true;
      }

      root.classList.add('is-result');
      if (!copy.main) {
        root.classList.add('is-result--miss');
      }
      if (copy.claim) {
        wonVariantIds = variantIdList(copy.variantIds);
        if (!wonVariantIds.length) wonVariantIds = configuredVariantIds(copy.prizeCode);
        wonPrizeCode = copy.prizeCode || '';
        root.classList.add('is-win');
        showClaim(resolveClaimState());
      } else {
        wonVariantIds = [];
        wonPrizeCode = '';
        hideClaim();
      }
    }

    const CART_HEADERS = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    async function loadCart() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('load cart ' + res.status);
      return res.json();
    }

    function cartHasItems(cart) {
      if (!cart) return false;
      if (Number(cart.item_count) > 0) return true;
      return Array.isArray(cart.items) && cart.items.length > 0;
    }

    function prizeLineMatches(item, prizeCode, variantIds) {
      if (!item) return false;
      if (String(prizeCode || '').trim() && item.properties && item.properties._ShokzSquad_lottery_prize_ === String(prizeCode).trim()) {
        return true;
      }
      return (variantIds || []).map(Number).filter(Boolean).indexOf(Number(item.variant_id || item.id)) !== -1;
    }

    // 清空购物车：先 clear.js，再读一次；仍有商品就按行把数量设为 0。
    async function clearCartThoroughly() {
      const cleared = await fetch('/cart/clear.js', { method: 'POST', headers: CART_HEADERS });
      if (!cleared.ok) throw new Error('clear ' + cleared.status);

      const cart = await loadCart();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!cartHasItems(cart) || !items.length) return;

      const updates = {};
      items.forEach(function (item) {
        if (item && item.key) updates[item.key] = 0;
      });
      const updated = await fetch('/cart/update.js', {
        method: 'POST',
        headers: CART_HEADERS,
        body: JSON.stringify({ updates }),
      });
      if (!updated.ok) throw new Error('zero remaining lines ' + updated.status);
    }

    // 加购后核对：购物车必须只含本次奖品，否则视为失败。
    async function cartContainsOnlyPrize(prizeCode, variantIds) {
      const cart = await loadCart();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.length) return false;
      return items.every(function (item) {
        return prizeLineMatches(item, prizeCode, variantIds);
      });
    }

    // 兑奖失败：停在当前页，提示失败，保留 Claim 按钮以便重试。
    function showClaimFailure() {
      const tpl = (resultTemplates && resultTemplates.claimError) || {
        heading: 'Unable to redeem',
        sub: 'Something went wrong while preparing your prize. Please try again.',
      };
      root.classList.remove('is-result', 'is-result--miss', 'is-win');
      if (h2El) {
        h2El.hidden = false;
        h2El.innerHTML = tpl.heading || '';
      }
      if (descEl) descEl.textContent = tpl.sub || '';
      if (resultSubEl) {
        resultSubEl.textContent = '';
        resultSubEl.hidden = true;
      }
      root.classList.add('is-result');
      showClaim(resolveClaimState());
    }

    const claimButton = root.querySelector('[data-lottery-claim="ready"]');
    let claimBusy = false;
    if (claimButton) {
      claimButton.addEventListener('click', async function () {
        if (claimBusy || claimButton.disabled) return;
        const prizeCode = wonPrizeCode;
        claimBusy = true;
        claimButton.classList.add('loading');
        claimButton.setAttribute('aria-busy', 'true');
        const spinner = claimButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // 库存检查：只有奖品仍有可售变体时才继续加购。
        let ids = [];
        try {
          ids = await availableVariantIdsForPrize(prizeCode);
        } catch (e) {
          ids = [];
        }
        wonVariantIds = ids.slice();
        console.log('[roulette claim] 1. 点击 Claim now', { variantIds: ids, prizeCode: prizeCode });
        if (!ids.length) {
          console.warn('[roulette claim] 中止：奖品库存不足 / 无可用库存可发奖');
          showOutOfStock();
          claimBusy = false;
          claimButton.classList.remove('loading');
          claimButton.removeAttribute('aria-busy');
          if (spinner) spinner.classList.add('hidden');
          return;
        }
        const items = ids.map(function (id) {
          const line = { id: id, quantity: 1 };
          if (prizeCode) line.properties = { _ShokzSquad_lottery_prize_: prizeCode };
          return line;
        });
        let step = '清空购物车';
        try {
          console.log('[roulette claim] 2. 清空购物车 (clear.js + 读车 + 按行归零)');
          await clearCartThoroughly();

          step = '加购中奖商品';
          console.log('[roulette claim] 3. 加购中奖商品', items);
          const added = await fetch('/cart/add.js', {
            method: 'POST',
            headers: CART_HEADERS,
            body: JSON.stringify({ items: items }),
          });
          const addedBody = await added.json().catch(function () { return null; });
          if (!added.ok) throw new Error((addedBody && (addedBody.description || addedBody.message)) || ('add ' + added.status));
          console.log('[roulette claim] 4. 加购成功', addedBody);

          step = '核对购物车';
          const onlyPrize = await cartContainsOnlyPrize(prizeCode, ids);
          if (!onlyPrize) throw new Error('cart still contains non-prize items');
          console.log('[roulette claim] 5. 购物车仅含奖品，校验通过');

          step = '跳转结账页';
          console.log('[roulette claim] 6. 跳转 /checkout');
          window.location.href = '/checkout';
        } catch (e) {
          console.error('[roulette claim] 失败，停在：' + step, e);
          claimBusy = false;
          claimButton.classList.remove('loading');
          claimButton.removeAttribute('aria-busy');
          const spinner = claimButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          showClaimFailure();
        }
      });
    }

    function setResultBlockedCopy(actStatus) {
      const copy = actStatusResultCopy[actStatus];
      if (!copy) return;
      setResultCopy(copy);
    }

    function delay(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    const guestClaim = root.querySelector('[data-lottery-claim="guest"]');
    if (guestClaim) guestClaim.setAttribute('href', claimLoginHref());

    /**
     * Logged-in customers whose account email matches the entered email.
     * Register first (us-hub-register), then the wrapped prize-draw. The
     * backend writes the member tag from that pair. An already-registered
     * member still continues to the draw.
     */
    async function ensureMemberRegistered() {
      try {
        const res = await ShokzMember.joinMember();
        if (res && (res.code === 0 || res.code === '0')) return true;
      } catch (e) {}
      if (typeof ShokzMember.isMember === 'function' && ShokzMember.isMember()) return true;
      const info = await ShokzMember.getMemberData().catch(function () { return null; });
      return !!(info && (info.code === 0 || info.code === '0') && info.data);
    }

    function mapApiPrize(data) {
      const payload = data || {};
      const map = Array.isArray(brandDayLotteryAuth.settings().prizeMap)
        ? brandDayLotteryAuth.settings().prizeMap
        : [];

      // draw 用 priceId；query 用 prize_id；prizeId 为未来重命名兼容。
      const apiPrizeKey =
        payload.priceId != null
          ? payload.priceId
          : payload.prize_id != null
            ? payload.prize_id
            : payload.prizeId;
      const actPriceId = payload.actPriceId != null ? payload.actPriceId : payload.act_price_id;
      const priceName = String(
        payload.priceName != null
          ? payload.priceName
          : payload.price_name != null
            ? payload.price_name
            : payload.award_name != null
              ? payload.award_name
              : payload.prize_name != null
                ? payload.prize_name
                : ''
      ).trim();

      const prizeFromCode = (code) => {
        const key = prizeCodeToKey[String(code)];
        if (!key) return null;
        const meta = { ...prizeKeyToPrizeMeta[key] };
        return { kind: 'prize', prizeKey: key, meta: meta };
      };

      const almostOutcome = (respKey) => {
        const isEntity = Number(payload.isEntity != null ? payload.isEntity : payload.is_entity);
        return {
          kind: 'almost',
          prizeKey: respKey,
          meta: {
            type: isEntity === 1 ? 'product' : 'coupon',
            name: isEntity === 1 ? priceName : undefined,
            amount: isEntity === 1 ? undefined : priceName,
            headingOverride: '',
            subOverride: '',
          },
        };
      };

      // 1) 直接按 actId + priceId 拼 key 命中奖品块。
      if (apiPrizeKey != null && apiPrizeKey !== '') {
        const respKey = buildPrizeKey(actId, apiPrizeKey);
        if (validPrizeKeys.has(respKey)) {
          const meta = { ...prizeKeyToPrizeMeta[respKey] };
          return { kind: 'prize', prizeKey: respKey, meta: meta };
        }
        if (almostPrizeKey && respKey === almostPrizeKey) {
          return almostOutcome(respKey);
        }
      }

      // 2) 主路径：数字 priceId / prize_id → prizeMap.price_id → 扇区 prize_id。
      if (apiPrizeKey != null && apiPrizeKey !== '') {
        const apiNum = Number(apiPrizeKey);
        if (!Number.isNaN(apiNum)) {
          for (const entry of map) {
            if (!entry || entry.price_id == null || !entry.prize_id) continue;
            if (Number(entry.price_id) === apiNum) {
              if (String(entry.prize_id) === 'miss') {
                return { kind: 'miss', meta: fallbackMeta };
              }
              const prize = prizeFromCode(entry.prize_id);
              if (prize) return prize;
            }
          }
        }
      }

      // 3) 后备：actPriceId → prizeMap.act_price_id（库表 id，可能变化）。
      if (actPriceId != null && actPriceId !== '') {
        for (const entry of map) {
          if (!entry || entry.act_price_id == null || !entry.prize_id) continue;
          if (Number(entry.act_price_id) === Number(actPriceId)) {
            if (String(entry.prize_id) === 'miss') {
              return { kind: 'miss', meta: fallbackMeta };
            }
            const prize = prizeFromCode(entry.prize_id);
            if (prize) return prize;
          }
        }
      }

      // 4) 名称兜底：award_names 命中 priceName。
      for (const entry of map) {
        if (!entry || !entry.prize_id) continue;
        const aliases = Array.isArray(entry.award_names) ? entry.award_names : [];
        if (
          priceName &&
          aliases.map(function (n) { return String(n).trim(); }).includes(priceName)
        ) {
          if (String(entry.prize_id) === 'miss') {
            return { kind: 'miss', meta: fallbackMeta };
          }
          const prize = prizeFromCode(entry.prize_id);
          if (prize) return prize;
        }
      }

      // 5) 未中奖兜底。
      return { kind: 'miss', meta: fallbackMeta };
    }

    /**
     * prize-query 单条记录 → 中奖结果。参考 section-member-lottery.js 的
     * resolvePrizeFromApi：query 的 prize_id 是数字（库表 prize_id，等于
     * prizeMap.price_id），不是字符串码。按 price_id → prizeMap.price_id →
     * 扇区 prize_id（字符串码，含 "miss"）映射。
     * 返回 null 表示该记录不属于本活动（其他活动的记录，跳过）。
     */
    function mapQueryPrize(record) {
      const payload = record || {};
      const map = Array.isArray(brandDayLotteryAuth.settings().prizeMap)
        ? brandDayLotteryAuth.settings().prizeMap
        : [];
      const apiPrizeKey =
        payload.prize_id != null
          ? payload.prize_id
          : payload.priceId != null
            ? payload.priceId
            : payload.prizeId;

      if (apiPrizeKey != null && apiPrizeKey !== '') {
        const apiNum = Number(apiPrizeKey);
        if (!Number.isNaN(apiNum)) {
          // 主键：数字 prize_id → prizeMap.price_id → 扇区 prize_id（字符串码）
          for (const entry of map) {
            if (!entry || entry.price_id == null || !entry.prize_id) continue;
            if (Number(entry.price_id) === apiNum) {
              if (String(entry.prize_id) === 'miss') {
                return { kind: 'miss', meta: fallbackMeta };
              }
              const key = prizeCodeToKey[String(entry.prize_id)];
              if (key) {
                const meta = { ...prizeKeyToPrizeMeta[key] };
                return { kind: 'prize', prizeKey: key, meta: meta };
              }
            }
          }
        }
        // 后备：直接按 actId + prize_id 拼 key 命中奖品块（未中奖不在块内，走 miss）
        const respKey = buildPrizeKey(actId, apiPrizeKey);
        if (validPrizeKeys.has(respKey)) {
          const meta = { ...prizeKeyToPrizeMeta[respKey] };
          return { kind: 'prize', prizeKey: respKey, meta: meta };
        }
      }
      // 该记录不属于本活动 / 无法识别 → 跳过
      return null;
    }

    /**
     * 已参与（no_finish）时，用 prize-query 还原该邮箱之前的中奖结果。
     * 失败（接口异常 / 无记录）返回 null，由调用方回退到 blocked 提示。
     */
    async function restorePrizeFromQuery(email) {
      const enteredEmail = String(email || '').trim();
      if (!enteredEmail) return null;
      try {
        const json = await brandDayLotteryAuth.prizeQuery(enteredEmail, actId);
        const data = (json && json.data) || {};
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data && data.list)
            ? data.list
            : Array.isArray(data && data.records)
              ? data.records
              : Array.isArray(data && data.rows)
                ? data.rows
                : [];
        if (!rows.length) return null;
        // 记录按 date_time 倒序（最新在前），且混有本活动与其它活动的记录；
        // 取第一条能映射到本活动的记录（中奖 / 未中奖）。
        for (let i = 0; i < rows.length; i++) {
          const outcome = mapQueryPrize(rows[i]);
          if (outcome) {
            outcome.repeatEntry = true;
            return outcome;
          }
        }
        return null;
      } catch (e) {
        return null;
      }
    }

    /**
     * 留邮提交：邮箱未注册，或已注册但还不是 ShokzSquad 会员时，
     * 调 us-shopify-register 自动入会。已是会员则跳过。失败不阻断抽奖。
     */
    async function autoRegisterEnteredEmail(email) {
      const targetEmail = String(email || '').trim();
      if (!targetEmail) return false;

      // 已是会员（登录态 + 邮箱一致 + isMember）→ 无需注册，视为成功。
      if (customerLoggedIn() && accountEmailMatches(targetEmail) && ShokzMember.isMember()) return true;

      // 已是会员（按邮箱查询到会员信息）→ 视为成功。
      try {
        const info = await ShokzMember.getMemberInfoByEmail(targetEmail);
        const codeOk = info && (info.code === 0 || info.code === '0');
        if (codeOk && ShokzMember.hasMemberInfoPayload(info)) return true;
      } catch (e) {}

      const baseUrl = String((window.shokzApiSettings && window.shokzApiSettings.apiBaseUrl) || '').replace(/\/$/, '');
      if (!baseUrl) return false;

      const form = new FormData();
      form.append('email', targetEmail);
      form.append('registration_source', ShokzMember.getRegistrationSource());
      form.append('source_url', window.location.href);

      const res = await (window.originFetch || fetch)(baseUrl + '/api/v1/us-member/us-shopify-register', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'X-Site': 'us',
          Authorization: ShokzMember.baseHeaders.Authorization,
        },
        body: form,
      });
      // 注册失败（HTTP 非 2xx）→ 不允许抽奖。
      return !!(res && res.ok);
    }

    // 给一个 promise 加超时，返回 { value } / { error } / { timedOut: true }。
    function withTimeout(promise, ms) {
      return new Promise((resolve) => {
        let done = false;
        const finish = (result) => {
          if (done) return;
          done = true;
          window.clearTimeout(timerId);
          resolve(result);
        };
        const timerId = window.setTimeout(() => finish({ timedOut: true }), ms);
        promise.then(
          (value) => finish({ value: value }),
          (error) => finish({ error: error })
        );
      });
    }

    // 注册链：游客/非会员自动入会 + 登录态确保会员注册。
    // 失败（含超时）返回 { ok: false }，调用方阻断抽奖。
    async function registerForDraw(email) {
      const enteredEmail = String(email || '').trim();
      if (!enteredEmail) return { ok: false, reason: 'empty' };

      const reg1 = await withTimeout(autoRegisterEnteredEmail(enteredEmail), registerTimeoutMs);
      if (reg1.timedOut) return { ok: false, reason: 'register_timeout' };
      if (reg1.error || !reg1.value) return { ok: false, reason: 'register_failed' };

      if (customerLoggedIn() && accountEmailMatches(enteredEmail)) {
        const reg2 = await withTimeout(ensureMemberRegistered(), registerTimeoutMs);
        if (reg2.timedOut) return { ok: false, reason: 'register_timeout' };
        if (reg2.error || !reg2.value) return { ok: false, reason: 'register_failed' };
      }

      return { ok: true };
    }

    // 抽奖链：get-token + prize-draw（no_finish 时再 prize-query），单独超时。
    async function drawPrize(email) {
      const enteredEmail = String(email || '').trim();
      const run = async () => {
        const json = await brandDayLotteryAuth.prizeDraw(enteredEmail, actId);
        const data = (json && json.data && json.data.data) || (json && json.data) || {};
        // act_status 可能藏在 data / json.data / json 三层，逐层取。
        const actStatus = String(
          (data && (data.act_status || data.actStatus)) ||
            (json && json.data && (json.data.act_status || json.data.actStatus)) ||
            (json && (json.act_status || json.actStatus)) ||
            ''
        ).trim().toLowerCase();
        if (actStatus === 'no_finish') {
          // 已参与：还原之前的中奖结果（prize-query），而不是直接弹「已参与」。
          // 还原出 prize/miss 后走正常结果页 + 领取入口（未登录 → 登录入口，
          // 已登录且邮箱一致 → 领取入口）。查询失败则回退到 blocked 提示。
          const restored = await restorePrizeFromQuery(enteredEmail);
          if (restored) return restored;
          return { kind: 'blocked', actStatus: 'no_finish', skipSpin: true };
        }
        if (actStatus === 'no_finish_ip' || actStatus === 'no_login') {
          return { kind: 'blocked', actStatus: actStatus };
        }
        // 抽奖过快 / 抽奖失败 / 系统异常 → 显示对应文案，不重置表单（不可立即重试）。
        if (actStatus === 'the lottery is too fast' || actStatus === 'lottery_error' || actStatus === 'system_error') {
          return { kind: 'blocked', actStatus: actStatus };
        }
        // miss / no_win 是真实未中奖。
        if (actStatus === 'miss' || actStatus === 'no_win') {
          return { kind: 'miss', meta: fallbackMeta };
        }
        // 其余非空 act_status（token_invalid 等）→ 按未中奖展示。
        if (actStatus) {
          return { kind: 'error', reason: 'api', actStatus: actStatus, meta: fallbackMeta };
        }
        // 无 act_status 且 data 无任何可识别开奖字段 → 请求失败。
        if (
          !data ||
          !(
            data.priceId != null ||
            data.prize_id != null ||
            data.prizeId != null ||
            data.actPriceId != null ||
            data.act_price_id != null ||
            data.priceName != null ||
            data.price_name != null ||
            data.award_name != null ||
            data.prize_name != null
          )
        ) {
          return { kind: 'blocked', actStatus: 'request_failed' };
        }
        return mapApiPrize(data);
      };

      const res = await withTimeout(run(), drawTimeoutMs);
      if (res.timedOut) return { kind: 'error', reason: 'draw_timeout' };
      if (res.error) {
        // 接口返回业务错误（如 code 400）→ 请求失败；其余网络异常按未中奖展示。
        if (res.error && res.error.lotteryBusinessCode != null) {
          return { kind: 'blocked', actStatus: 'request_failed' };
        }
        return { kind: 'error', reason: 'draw_network' };
      }
      return res.value;
    }

    async function fetchPrize(email) {
      const enteredEmail = String(email || '').trim();
      if (!enteredEmail) return { kind: 'miss', meta: fallbackMeta };

      // 注册失败 → 不允许抽奖，返回 error 提示重试。
      const reg = await registerForDraw(enteredEmail);
      if (!reg.ok) {
        return { kind: 'error', reason: 'register', actStatus: reg.reason };
      }

      return drawPrize(enteredEmail);
    }

    function landWheelAtPrize({ wheelImgEl, prizeKey, targetDeg }) {
      if (!wheelImgEl) return Promise.resolve();

      return new Promise((resolve) => {
        const startNorm = normalizeDeg(getRotationDeg(wheelImgEl));
        const deg = targetDeg != null ? targetDeg : getSectorDeg(prizeKey);
        const targetNorm = normalizeDeg(deg);
        const deltaToTarget = normalizeDeg(targetNorm - startNorm);
        const extraFullTurns = deltaToTarget === 0 ? 1 : 0;
        const finalDeg = startNorm + extraFullTurns * 360 + deltaToTarget;

        wheelImgEl.classList.remove('is-spinning');
        wheelImgEl.style.transition = '';
        wheelImgEl.style.transform = `rotate(${startNorm}deg)`;
        void wheelImgEl.offsetHeight;

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            wheelImgEl.style.transition = `transform ${decelMs}ms ease-out`;
            wheelImgEl.style.transform = `rotate(${finalDeg}deg)`;

            let resolved = false;
            const cleanup = () => {
              if (resolved) return;
              resolved = true;
              wheelImgEl.removeEventListener('transitionend', onTransitionEnd);
              window.clearTimeout(fallbackTimerId);
              resolve();
            };

            const onTransitionEnd = (e) => {
              if (e.target !== wheelImgEl) return;
              if (e.propertyName !== 'transform') return;
              cleanup();
            };

            wheelImgEl.addEventListener('transitionend', onTransitionEnd);
            const fallbackTimerId = window.setTimeout(cleanup, decelMs + 80);
          });
        });
      });
    }

    async function spinWheel(email, prizePromiseOverride) {
      const wheelImgEl = getActiveWheelImg();
      if (!wheelImgEl) return { kind: 'miss', meta: fallbackMeta };

      // 提交时（captureSubmitIntent）就已经起转；这里只兜底还没转的情况。
      if (!wheelImgEl.classList.contains('is-spinning')) {
        wheelImgEl.style.transition = '';
        wheelImgEl.style.setProperty('--wheel-rot', '0deg');
        wheelImgEl.classList.add('is-spinning');
      }

      // 优先复用提交瞬间已发起的抽奖请求；未提供则现场发起（兜底）。
      const prizePromise = prizePromiseOverride || fetchPrize(email);
      const spinInfiniteMs = spinTotalMs - decelMs;

      // 转盘在提交时就开始转，这里只把剩下的「匀速旋转」时长等掉，
      // 让结果在旋转期间并行获取，而不是点击后干等接口再起转。
      const elapsed = spinStartedAtMs ? Date.now() - spinStartedAtMs : 0;
      spinStartedAtMs = 0;
      const remaining = Math.max(0, spinInfiniteMs - elapsed);
      if (remaining > 0) await delay(remaining);

      // fetchPrize 内部已按「注册 / 抽奖」各自 30s 兜底，这里直接等结果即可，
      // 不再用外层 5s race 把注册/抽奖结果提前截断成 miss。
      const outcome = await prizePromise;

      // "miss" / "blocked" land on the miss sector; "almost" lands on the almost
      // sector; a wheel prize lands on its block sector. Non-timeout errors are
      // presented as a miss, so they stop on the miss sector too.
      if (outcome.kind === 'blocked' || outcome.kind === 'miss') {
        await landWheelAtPrize({ wheelImgEl, targetDeg: getMissSectorDeg() });
        return outcome;
      }
      if (outcome.kind === 'almost') {
        await landWheelAtPrize({ wheelImgEl, targetDeg: getAlmostSectorDeg() });
        return outcome;
      }
      if (outcome.kind === 'error') {
        await landWheelAtPrize({ wheelImgEl, targetDeg: getMissSectorDeg() });
        return outcome;
      }

      const prizeKey = outcome.prizeKey;
      await landWheelAtPrize({ wheelImgEl, prizeKey });
      return outcome;
    }

    // 提交瞬间就起转盘（无限旋转），不用等 Klaviyo 确认；结果在旋转期间并行获取。
    function startInfiniteSpin() {
      const wheelImgEl = getActiveWheelImg();
      if (!wheelImgEl) return;
      if (!wheelImgEl.classList.contains('is-spinning')) {
        wheelImgEl.style.transition = '';
        wheelImgEl.style.setProperty('--wheel-rot', '0deg');
        wheelImgEl.classList.add('is-spinning');
      }
      spinStartedAtMs = Date.now();
    }

    // Shared "the user is submitting" capture: flags the submit + grabs the email,
    // starts the wheel, and — now that 留邮（Klaviyo）与注册（register）解耦 — fires
    // the register → draw chain immediately. Klaviyo 留邮 runs in parallel with its
    // own bounded retry (scheduleKlaviyoRetry), and no longer gates the draw.
    function captureSubmitIntent() {
      const { input } = findInner();
      const email = input && input.value ? input.value.trim() : '';
      // 空邮箱：不转盘、不抽奖，交给 Klaviyo 自己的校验提示。
      if (!email) return;

      buttonClicked = true;
      buttonClickedAtMs = Date.now();
      submittedEmail = email;
      startInfiniteSpin();

      // 留邮与注册解耦：提交瞬间就发起「注册 → 抽奖」链（fetchPrize 内部先注册再抽奖），
      // 不再等 Klaviyo 留邮成功。留邮由 Klaviyo 自己并行提交，失败走 scheduleKlaviyoRetry。
      if (!pendingDrawPromise) {
        // 预热 get-token（与注册并行），抽奖接口复用时不再等待。
        brandDayLotteryAuth.ensureToken(false).catch(function () {});
        pendingDrawPromise = fetchPrize(submittedEmail);
        handleDrawResult(submittedEmail);
        scheduleKlaviyoRetry();
      }
    }

    // 等待「注册 → 抽奖」结果，落扇区并渲染结果。与 Klaviyo 留邮完全解耦。
    async function handleDrawResult(email) {
      let spinResult;
      try {
        spinResult = await spinWheel(email, pendingDrawPromise);
      } catch (e) {
        spinResult = { kind: 'miss', meta: fallbackMeta };
      }
      finishDraw(spinResult);
    }

    // 留邮兜底：监听 klaviyoForms submit 成功；未确认则最多再重试一次（重新点击 Klaviyo 提交）。
    function scheduleKlaviyoRetry() {
      window.clearTimeout(klaviyoRetryTimerId);
      klaviyoRetryTimerId = 0;
      if (klaviyoSubmitConfirmed) return;
      klaviyoRetryTimerId = window.setTimeout(function () {
        klaviyoRetryTimerId = 0;
        if (completed || klaviyoSubmitConfirmed) return;
        // 输入框已消失 = 留邮已成功（即使没收到 klaviyoForms 事件）→ 视为成功。
        const { input } = findInner();
        if (!input || isEmailInputGone()) {
          klaviyoSubmitConfirmed = true;
          fireEmailSubscribe('success');
          return;
        }
        // 留邮失败：最多重试一次（参考 2026 纽马 newyork-marathon-lottery 的重试兜底）。
        // 这里只重新触发 Klaviyo 提交，不重复走 captureSubmitIntent（避免重置转盘计时）。
        if (klaviyoRetryCount < klaviyoMaxRetries) {
          const submitBtn = klaviyoContainer.querySelector('button');
          if (submitBtn && !submitBtn.disabled) {
            klaviyoRetryCount += 1;
            retrySubmitting = true;
            submitBtn.click();
            retrySubmitting = false;
          } else if (klaviyoRetryDeferral < 4) {
            // 按钮暂时不可点时不消耗唯一一次重试，短间隔后再试。
            klaviyoRetryDeferral += 1;
          } else {
            klaviyoRetryCount += 1;
          }
          // 再等一轮，给这次重试一个确认窗口（klaviyoForms submit 或输入框消失）。
          scheduleKlaviyoRetry();
        } else {
          // 重试已用尽仍未确认留邮成功 → 上报失败。
          fireEmailSubscribe('fail');
        }
      }, klaviyoRetryInterval);
    }

    // 渲染抽奖结果（从原 MutationObserver 抽出的完成逻辑，不再依赖 Klaviyo 留邮）。
    function finishDraw(spinResult) {
      if (completed) return;
      completed = true;
      observer.disconnect();
      hideAllWheelHints();
      window.clearTimeout(submitAbortTimerId);

      const formContent = root.querySelector('.form-content');
      if (formContent) formContent.style.display = 'none';

      // 留邮 subscribe 埋点已改到 Klaviyo 留邮确认处（status = success / fail），
      // 此处不再把抽奖结果（win/lose/blocked）塞进 subscribe 事件。

      if (spinResult.kind === 'error') {
        const isTimeout =
          spinResult.reason === 'draw_timeout' || spinResult.actStatus === 'register_timeout';
        // 超时仍提示重试。其余 error 兜底情况太多，按未中奖展示，转盘停在未中奖扇区。
        if (!isTimeout) {
          spinResult = { kind: 'miss', meta: fallbackMeta };
        } else {
          showWheelResult({
            heading: 'Request timed out',
            sub: 'Please try again.',
          });
          resetWheelMotion();
          const formContentEl = root.querySelector('.form-content');
          if (formContentEl) formContentEl.style.display = '';
          buttonClicked = false;
          buttonClickedAtMs = 0;
          submittedEmail = '';
          spinStartedAtMs = 0;
          wonVariantIds = [];
          wonPrizeCode = '';
          pendingDrawPromise = null;
          completed = false;
          observer.observe(klaviyoContainer, {
            childList: true,
            subtree: true,
            attributeFilter: ['class'],
          });
          scheduleReserveLotteryStageHeight();
          return;
        }
      }

      if (spinResult.kind === 'blocked') {
        const blockedCopy = actStatusResultCopy[spinResult.actStatus] || {};
        if (spinResult.actStatus === 'no_finish') {
          if (promptEl) promptEl.textContent = blockedCopy.heading || "Already spun";
          const agreementWrap = root.querySelector('.agreement');
          if (agreementWrap) agreementWrap.style.display = 'none';
        }
        showWheelResult(blockedCopy);
      } else {
        // Win / miss / almost → original right-side copy replacement. "almost"
        // reads the same copy as a true miss, but still lands on its own sector.
        const meta = spinResult.meta || fallbackMeta;
        if (spinResult.kind === 'almost') meta.type = 'miss';
        const copy = buildResultCopy(meta);
        if (spinResult.kind === 'prize') {
          copy.variantIds = meta.variantIds || [];
          copy.prizeCode = meta.prizeCode || '';
        }
        // 重复留邮回显（中奖或未中奖）：标题改为「已参与」，正文保留原结果明细。
        if (spinResult.repeatEntry) {
          copy.heading = "You've already joined!";
        }
        setResultCopy(copy);
      }
      scheduleReserveLotteryStageHeight();
    }

    // ===== Cloudflare Turnstile（留邮人机验证）=====
    // sitekey 由 section settings 下发（cfg.turnstileSiteKey），为空则完全跳过验证逻辑。
    // 流程：渲染 widget 拿 token → 提交时 POST 后端 verify → 通过才放行 Klaviyo 提交。
    // 脚本加载失败或挑战报错时放行提交，只有后端明确拒绝才锁定。
    const turnstileSiteKey = String(cfg.turnstileSiteKey || '').trim();
    const turnstileScriptSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    const turnstileVerifyEndpoint = 'https://global.shokz.com/api/v1/email-risk/verify';
    const turnstileFailureMessage = 'Unable to submit at this time. Please try again later.';
    const turnstileLoadTimeoutMs = 8000;
    let turnstileToken = '';
    let turnstileWidgetId = '';
    let turnstileVerifyPromise = null;
    let turnstileVerifyStatus = 'idle'; // idle | pending | passed | rejected | error
    let isTurnstileVerifying = false;
    let isTurnstileLocked = false;
    let allowVerifiedSubmit = false;
    let turnstileBypass = false;
    let turnstileDisabledButton = false;
    let turnstilePollTimer = 0;
    let turnstileLoadTimer = 0;

    function getTurnstileHost() {
      return root.querySelector('[data-roulette-turnstile]');
    }

    function resetTurnstileToken() {
      turnstileToken = '';
      turnstileVerifyPromise = null;
      turnstileVerifyStatus = 'idle';
      applyTurnstileButtonState();
    }

    // 作废当前 token 并让 Turnstile 重新下发一个：Cloudflare token 一次性有效，
    // 提交放行后不复位会导致下一次提交复用旧 token（后端必然拒绝）。
    // 放行后的 Klaviyo 重试不依赖这个 token，因此这里不能把提交按钮禁用掉。
    function refreshTurnstileToken() {
      resetTurnstileToken();
      if (turnstileWidgetId !== '' && window.turnstile && window.turnstile.reset) {
        window.turnstile.reset(turnstileWidgetId);
      }
    }

    function markTurnstileUnavailable(reason) {
      if (turnstileBypass || isTurnstileLocked) return;
      turnstileBypass = true;
      window.clearTimeout(turnstilePollTimer);
      window.clearTimeout(turnstileLoadTimer);
      console.warn('[Turnstile] unavailable, allowing email submit', reason);
      applyTurnstileButtonState();
    }

    function armTurnstileLoadTimeout() {
      window.clearTimeout(turnstileLoadTimer);
      turnstileLoadTimer = window.setTimeout(function () {
        const host = getTurnstileHost();
        const widgetRendered = host && host.dataset.turnstileRendered === 'true';
        if (turnstileBypass || isTurnstileLocked || turnstileToken || widgetRendered) return;
        markTurnstileUnavailable('load timeout');
      }, turnstileLoadTimeoutMs);
    }

    function lockTurnstileAfterFailure() {
      isTurnstileLocked = true;
      window.clearTimeout(turnstilePollTimer);
      window.clearTimeout(turnstileLoadTimer);
      resetTurnstileToken();
      getTurnstileHost()?.classList.add('is-hidden');
      applyTurnstileButtonState();
      scheduleReserveLotteryStageHeight();
    }

    // 三态返回：'passed'（后端确认有效）/ 'rejected'（后端明确判定 token 无效，按风控处理）
    // / 'error'（网络或服务端异常，属可重试的临时故障，不做风控判定）。
    async function verifyTurnstileToken(token) {
      const formData = new FormData();
      formData.append('verify_token', token);

      let response;
      try {
        response = await fetch(turnstileVerifyEndpoint, {
          method: 'POST',
          headers: {
            'X-Site': 'us',
          },
          body: formData,
        });
      } catch (error) {
        console.warn('[Turnstile] verify request failed', error);
        return 'error';
      }

      // 非 2xx（服务端异常 / 限流）不代表 token 有问题，不能据此判定为机器人。
      if (!response.ok) return 'error';

      let result;
      try {
        result = await response.json();
      } catch (error) {
        console.warn('[Turnstile] verify response parse failed', error);
        return 'error';
      }
      return result?.data?.success === true ? 'passed' : 'rejected';
    }

    function startTurnstileVerification(token) {
      turnstileVerifyStatus = 'pending';
      turnstileVerifyPromise = verifyTurnstileToken(token)
        .then(function (outcome) {
          turnstileVerifyStatus = outcome;
          return outcome;
        })
        .catch(function (error) {
          turnstileVerifyStatus = 'error';
          console.warn('[Turnstile] verify request failed', error);
          return 'error';
        })
        .finally(function () {
          applyTurnstileButtonState();
        });
      applyTurnstileButtonState();
      return turnstileVerifyPromise;
    }

    // 只在校验进行中或后端明确拒绝后禁用按钮。缺 token 时保持可点，避免脚本失败把留邮堵死。
    function applyTurnstileButtonState() {
      if (!turnstileSiteKey) return;
      const { button } = findInner();
      if (!button) return;
      const shouldBlock = !turnstileBypass && (isTurnstileLocked || isTurnstileVerifying);
      if (shouldBlock) {
        if (!button.disabled) {
          button.disabled = true;
          turnstileDisabledButton = true;
        }
        return;
      }
      if (turnstileDisabledButton) {
        button.disabled = false;
        turnstileDisabledButton = false;
      }
    }

    function loadTurnstileScript() {
      if (window.turnstile || document.querySelector('script[src="' + turnstileScriptSrc + '"]')) return;

      const script = document.createElement('script');
      script.src = turnstileScriptSrc;
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        markTurnstileUnavailable('script error');
      };
      document.head.appendChild(script);
    }

    function renderTurnstileWidget() {
      const host = getTurnstileHost();
      if (turnstileBypass || isTurnstileLocked) return;
      if (!host || host.dataset.turnstileRendered === 'true') return;

      if (!window.turnstile) {
        loadTurnstileScript();
        window.clearTimeout(turnstilePollTimer);
        turnstilePollTimer = window.setTimeout(renderTurnstileWidget, 300);
        return;
      }

      window.clearTimeout(turnstilePollTimer);
      turnstileWidgetId = window.turnstile.render(host, {
        sitekey: turnstileSiteKey,
        callback: function (token) {
          window.clearTimeout(turnstileLoadTimer);
          turnstileToken = token;
          turnstileVerifyPromise = null;
          turnstileVerifyStatus = 'idle';
          applyTurnstileButtonState();
        },
        'expired-callback': function () {
          resetTurnstileToken();
          armTurnstileLoadTimeout();
          console.log('[Turnstile] token expired');
        },
        'error-callback': function () {
          resetTurnstileToken();
          console.warn('[Turnstile] challenge error');
          window.setTimeout(function () {
            if (!turnstileToken && !isTurnstileLocked) markTurnstileUnavailable('challenge error');
          }, 2000);
        },
      });

      host.dataset.turnstileRendered = 'true';
      applyTurnstileButtonState();
    }

    function showTurnstileMessage(message) {
      const host = getTurnstileHost();
      const formContent = host ? host.closest('.form-content') : null;
      if (!formContent) return;

      let messageEl = formContent.querySelector('.common-roulette__turnstile-message');
      if (!messageEl) {
        messageEl = document.createElement('div');
        messageEl.className = 'common-roulette__turnstile-message';
        messageEl.setAttribute('role', 'alert');
        formContent.appendChild(messageEl);
      }

      messageEl.textContent = message;
      window.clearTimeout(messageEl._hideTimer);
      messageEl._hideTimer = window.setTimeout(function () {
        messageEl.remove();
      }, 3000);
    }

    function releaseVerifiedSubmit(btn) {
      isTurnstileVerifying = false;
      applyTurnstileButtonState();
      if (!btn) return;
      allowVerifiedSubmit = true;
      btn.click();
    }

    // 提交闸门：Turnstile 未通过时拦截这次提交（不进入留邮/抽奖流程），
    // 后端校验通过后置位 allowVerifiedSubmit 并补一次点击完成原提交流程；
    // 放行后立即作废本次 token，下一次提交必须重新校验。
    // 重置 token 时不禁用按钮，避免吃掉 Klaviyo 的那一次重试。
    async function runTurnstileGate(btn) {
      if (turnstileBypass) {
        releaseVerifiedSubmit(btn);
        return;
      }
      if (!turnstileToken) {
        showTurnstileMessage('Please wait a moment and try again');
        return;
      }
      if (isTurnstileVerifying) {
        showTurnstileMessage('Please wait a moment and try again');
        return;
      }

      isTurnstileVerifying = true;
      applyTurnstileButtonState();

      try {
        const outcome =
          turnstileVerifyStatus === 'idle'
            ? await (turnstileVerifyPromise || startTurnstileVerification(turnstileToken))
            : turnstileVerifyStatus;

        if (outcome === 'rejected') {
          // 后端明确判定 token 无效：维持原有严格策略（锁定并拦截）。
          lockTurnstileAfterFailure();
          showTurnstileMessage(turnstileFailureMessage);
          return;
        }

        if (outcome !== 'passed') {
          // 网络/服务端异常属临时故障：不锁定，重新下发 token 后用户可直接重试。
          refreshTurnstileToken();
          showTurnstileMessage(turnstileFailureMessage);
          return;
        }

        releaseVerifiedSubmit(btn);
        refreshTurnstileToken();
      } catch (error) {
        console.warn('[Turnstile] verify request failed', error);
        refreshTurnstileToken();
        showTurnstileMessage(turnstileFailureMessage);
      } finally {
        isTurnstileVerifying = false;
        applyTurnstileButtonState();
      }
    }

    if (turnstileSiteKey) {
      armTurnstileLoadTimeout();
      renderTurnstileWidget();
    }

    if (!klaviyoContainer._rouletteDelegatedClick) {
      klaviyoContainer.addEventListener(
        'click',
        (e) => {
          // 留邮失败重试触发的程序化点击，不再重复走用户提交流程。
          if (retrySubmitting) return;
          const target = e.target;
          if (!(target instanceof Element)) return;
          const btn = target.closest('button');
          if (!btn || !klaviyoContainer.contains(btn)) return;
          // Turnstile 闸门：首次点击先做人机验证，通过后置位 allowVerifiedSubmit
          // 并补一次点击；验证不通过则拦截（不进入留邮/抽奖流程）。
          if (turnstileSiteKey && !turnstileBypass) {
            if (allowVerifiedSubmit) {
              allowVerifiedSubmit = false;
            } else {
              e.preventDefault();
              e.stopImmediatePropagation();
              void runTurnstileGate(btn);
              return;
            }
          }
          captureSubmitIntent();
        },
        true
      );
      // Pressing Enter in the email input submits the Klaviyo form natively (not
      // via a button click), so the click handler above never fires and the spin
      // is skipped. Capture the same submit intent on Enter so the spin runs too.
      // Turnstile 闸门开启时 Enter 同样先走人机验证，通过后以程序化点击完成提交。
      klaviyoContainer.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Enter' && e.keyCode !== 13) return;
          if (turnstileSiteKey && !turnstileBypass && !allowVerifiedSubmit) {
            e.preventDefault();
            e.stopImmediatePropagation();
            void runTurnstileGate(klaviyoContainer.querySelector('button'));
            return;
          }
          // We intentionally do NOT preventDefault — Klaviyo must still subscribe.
          captureSubmitIntent();
        },
        true
      );
      klaviyoContainer._rouletteDelegatedClick = true;
    }

    function checkAndApplyStyles() {
      const { button, input, form } = findInner();
      if (!button || !input || !form) return false;
      if (button.style.display === 'none' || button.style.visibility === 'hidden') return false;

      const mediaQuery = window.matchMedia('(min-width: 901px)');
      const updateStyles = (e) => {
        if (e.matches) {
          input.style.height = '48px';
          button.style.height = '48px';
          button.style.minWidth = '88px';
          button.style.padding = '0 20px';
          input.style.borderRadius = '4px 0 0 4px';
          input.style.border = '1px solid #fff';
          button.style.borderRadius = '0 4px 4px 0';
          button.style.fontSize = '16px';
        } else {
          input.style.height = '10.17811705vw';
          input.style.fontSize = '4.07124682vw';
          button.style.height = '10.17811705vw';
          button.style.padding = '0 2.54452926vw';
          button.style.fontSize = '4.07124682vw';
          input.style.borderRadius = '1.01781170vw 0 0 1.01781170vw';
          input.style.border = '0.25445293vw solid #fff';
          button.style.borderRadius = '0 1.01781170vw 1.01781170vw 0';
        }

        form.style.padding = '0';
        const firstDiv = form.querySelector('div');
        if (firstDiv) firstDiv.style.minHeight = 'unset';
        button.style.background = 'var(--roulette-btn)';
        button.style.color = 'var(--roulette-btn-text)';
        button.style.fontFamily = "'Shokz Sans Display'";
        button.style.fontWeight = '400';
        applyButtonEnablement(button);
        applyTurnstileButtonState();
      };

      if (!buttonHasAppeared) {
        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', updateStyles);
        } else {
          mediaQuery.addListener(updateStyles);
        }
        button.textContent = (cfg.copy && cfg.copy.formButtonLabel) || 'Enter';
        buttonHasAppeared = true;
      }

      updateStyles(mediaQuery);
      return true;
    }

    function retryApplyStyles() {
      if (retryCount >= maxRetries) return;
      if (!checkAndApplyStyles()) {
        retryCount++;
        window.setTimeout(retryApplyStyles, retryInterval);
      }
    }

    if (agreementEl) {
      agreementEl.addEventListener('change', () => {
        const { button } = findInner();
        applyButtonEnablement(button);
      });
    }

    // Klaviyo 表单异步渲染：observer 只负责「按钮/输入框出现后套样式」。
    // 不再用「输入框消失」作为抽奖触发信号——抽奖已与留邮解耦，由 handleDrawResult 驱动。
    const observer = new MutationObserver(function () {
      if (completed) return;
      const { button } = findInner();
      if (button) {
        if (!checkAndApplyStyles()) retryApplyStyles();
      }
    });

    observer.observe(klaviyoContainer, {
      childList: true,
      subtree: true,
      attributeFilter: ['class'],
    });

    retryApplyStyles();

    const prizeCount = (cfg.displayPrizes || []).length;
    if (prizeCount > 0 && typeof Swiper !== 'undefined') {
      const swiperEl = root.querySelector('.common-roulette-swiper');
      if (swiperEl) {
        const isDesktop = window.matchMedia('(min-width: 901px)').matches;
        const prizeSpaceBetween = isDesktop ? 12 : 10;

        const AUTO_SPEED = 10000;

        const prizeSwiper = new Swiper(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: prizeSpaceBetween,
          loop: true,
          speed: AUTO_SPEED,
          allowTouchMove: true,
          watchOverflow: true,
          autoplay: {
            delay: 0,
            disableOnInteraction: false,
          },
        });

        prizeSwiper.on('touchEnd', function () {
          this.params.speed = 300;
        });

        prizeSwiper.on('transitionEnd', function () {
          if (this.params.speed !== AUTO_SPEED) {
            this.params.speed = AUTO_SPEED;
            if (this.autoplay.running) {
              this.autoplay.stop();
              this.autoplay.start();
            }
          }
        });

        const carouselEl = root.querySelector('.prize-carousel');
        if (carouselEl) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  prizeSwiper.autoplay.start();
                } else {
                  prizeSwiper.autoplay.stop();
                }
              });
            },
            { threshold: 0.5 }
          );
          observer.observe(carouselEl);
        }
      }
    }

    // 赠品卡片图片点击 select_content 埋点
    root.querySelectorAll('.common-roulette__prize-card__media, .prize-cards__media').forEach(function (media) {
      media.addEventListener('click', function () {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event_parameters: null });
        window.dataLayer.push({
          event: 'ga4Event',
          event_name: 'select_content',
          event_parameters: {
            content_type: 'section',
            content_name: 'section_gift',
            button_name: 'Gift_Pack_Image',
          },
        });
      });
    });

    const initialHeadingHTML = h2El ? h2El.innerHTML : '';
    const initialHeadingHidden = h2El ? h2El.hidden : true;
    const initialDesc = descEl ? descEl.textContent : '';
    const displayPrizes = Array.isArray(cfg.displayPrizes) ? cfg.displayPrizes : [];

    function plainPrizeLabel(prize) {
      const raw = prize.resultName || prize.metaValue || prize.value || prize.name || '';
      return String(raw)
        .replace(/@br_pc/g, ' ')
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function measureResultHeight(copy) {
      const stage = root.querySelector('[data-lottery-stage]');
      if (!stage) return 0;
      const probe = document.createElement('div');
      probe.className = 'lottery-stage__result';
      probe.style.position = 'absolute';
      probe.style.left = '0';
      probe.style.top = '0';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.width = stage.clientWidth + 'px';
      probe.style.overflow = 'hidden';
      const heading = document.createElement('p');
      heading.className = 'common-roulette__subheading';
      heading.setAttribute('data-lottery-heading', '');
      heading.innerHTML = copy.heading || '';
      const desc = document.createElement('p');
      desc.className = 'desc';
      desc.setAttribute('data-lottery-desc', '');
      desc.textContent = copy.main || copy.sub || '';
      probe.appendChild(heading);
      probe.appendChild(desc);
      if (copy.main && copy.sub) {
        const sub = document.createElement('p');
        sub.className = 'result-sub';
        sub.textContent = copy.sub;
        probe.appendChild(sub);
      }
      if (copy.claim) {
        const button = document.createElement('span');
        button.className = 'common-roulette__claim';
        button.textContent = 'Sign in to claim';
        probe.appendChild(button);
      }
      stage.appendChild(probe);
      const height = probe.offsetHeight;
      probe.remove();
      return height;
    }

    function reserveLotteryStageHeight() {
      const stage = root.querySelector('[data-lottery-stage]');
      const idle = root.querySelector('[data-lottery-idle]');
      if (!stage || !idle || !stage.clientWidth) return;
      const copies = [buildResultCopy({ type: 'miss' })];
      displayPrizes.forEach(function (prize) {
        const label = plainPrizeLabel(prize);
        copies.push(buildResultCopy({
          type: prize.type || 'product',
          name: prize.type === 'coupon' ? undefined : label,
          amount: prize.type === 'coupon' ? label : undefined,
          headingOverride: prize.headingOverride || '',
          subOverride: prize.subOverride || '',
        }));
      });
      let maxHeight = idle.offsetHeight;
      copies.forEach(function (copy) {
        maxHeight = Math.max(maxHeight, measureResultHeight(copy));
      });
      if (maxHeight) stage.style.minHeight = maxHeight + 'px';
    }

    let stageHeightFrame = 0;
    function scheduleReserveLotteryStageHeight() {
      if (stageHeightFrame) return;
      stageHeightFrame = window.requestAnimationFrame(function () {
        stageHeightFrame = 0;
        reserveLotteryStageHeight();
      });
    }

    reserveLotteryStageHeight();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reserveLotteryStageHeight);
    }
    if (window.ResizeObserver) {
      const idle = root.querySelector('[data-lottery-idle]');
      const stage = root.querySelector('[data-lottery-stage]');
      if (idle && stage) {
        const idleObserver = new ResizeObserver(scheduleReserveLotteryStageHeight);
        idleObserver.observe(idle);
      }
    }
    window.addEventListener('resize', reserveLotteryStageHeight);

    function resetWheelMotion() {
      root.querySelectorAll('.wheel-img').forEach(function (wheel) {
        wheel.classList.remove('is-spinning');
        wheel.style.transition = '';
        wheel.style.transform = '';
        wheel.style.setProperty('--wheel-rot', '0deg');
      });
    }

    window.rouletteDebug = {
      reset: function () {
        resetWheelMotion();
        window.clearTimeout(submitAbortTimerId);
        window.clearTimeout(klaviyoRetryTimerId);
        buttonClicked = false;
        buttonClickedAtMs = 0;
        submittedEmail = '';
        completed = false;
        spinStartedAtMs = 0;
        pendingDrawPromise = null;
        klaviyoSubmitConfirmed = false;
        klaviyoRetryCount = 0;
        klaviyoRetryDeferral = 0;
        klaviyoRetryTimerId = 0;
        retrySubmitting = false;
        emailSubscribeFired = false;
        claimOverride = null;
        wonVariantIds = [];
        wonPrizeCode = '';
        claimBusy = false;
        const claimButton = root.querySelector('[data-lottery-claim="ready"]');
        if (claimButton) {
          claimButton.disabled = false;
          claimButton.classList.remove('loading');
          claimButton.removeAttribute('aria-busy');
          const spinner = claimButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
        root.classList.remove('is-result', 'is-result--miss', 'is-win');
        hideClaim();
        if (h2El) {
          h2El.innerHTML = initialHeadingHTML;
          h2El.hidden = initialHeadingHidden;
        }
        if (descEl) descEl.textContent = initialDesc;
        if (promptEl) promptEl.textContent = initialPromptText;
        const formContent = root.querySelector('.form-content');
        if (formContent) formContent.style.display = '';
        if (resultSubEl) {
          resultSubEl.textContent = '';
          resultSubEl.hidden = true;
        }
      },
      spin: function () {
        const wheel = getActiveWheelImg();
        if (!wheel) return;
        wheel.style.transition = '';
        wheel.style.setProperty('--wheel-rot', '0deg');
        wheel.classList.add('is-spinning');
      },
      win: async function (rank) {
        const index = Number(rank) - 1;
        const prize = displayPrizes[index];
        if (!prize) {
          console.warn('rouletteDebug.win: no prize at rank ' + rank + '. Available: 1-' + displayPrizes.length);
          return;
        }
        const wheel = getActiveWheelImg();
        if (wheel) wheel.classList.remove('is-spinning');
        const deg = isMobile() ? prize.sectorDegMobile : prize.sectorDegDesktop;
        await landWheelAtPrize({ wheelImgEl: wheel, targetDeg: Number(deg) || 0 });
        const label = plainPrizeLabel(prize);
        const copy = buildResultCopy({
          type: prize.type || 'product',
          name: prize.type === 'coupon' ? undefined : label,
          amount: prize.type === 'coupon' ? label : undefined,
          headingOverride: prize.headingOverride || '',
          subOverride: prize.subOverride || '',
        });
        copy.variantIds = prize.variantIds || [];
        copy.prizeCode = prize.prizeCode || '';
        setResultCopy(copy);
      },
      claim: function (state) {
        const allowed = { guest: true, ready: true, claimed: true };
        if (!allowed[state]) {
          console.warn("rouletteDebug.claim('guest' | 'ready' | 'claimed')");
          return;
        }
        claimOverride = state;
        if (root.classList.contains('is-win')) showClaim(state);
      },
      loading: function () {
        root.classList.add('is-result', 'is-win');
        showClaim('ready');
        const button = root.querySelector('[data-lottery-claim="ready"]');
        if (!button) return;
        claimBusy = true;
        button.classList.add('loading');
        button.setAttribute('aria-busy', 'true');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        console.log('[roulette claim] 预览 CTA loading。rouletteDebug.reset() 恢复');
      },
    };
    console.info("rouletteDebug.reset() 恢复 | rouletteDebug.spin() 一直转 | rouletteDebug.win(1) 第1等奖 | rouletteDebug.claim('guest'|'ready'|'claimed') | rouletteDebug.loading() CTA loading");
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBrandDayLotteries);
} else {
  initBrandDayLotteries();
}
